import {
  faAlignJustify,
  faCalendar,
  faCheck,
  faEllipsisH,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import TemplateDaysToCompleteEditModal from 'components/modals/TemplateDaysToCompleteEditModal';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Dropdown,
  Form,
  OverlayTrigger,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import AgentPhotoService from 'services/AgentPhoto.service';

const TemplateNewTaskCard = ({
  onCancel,
  onTaskAdded,
  isGroupTitle = false,
  agents = [],
  isLoading: externalLoading = false, // Loading externo do componente pai
  onTaskAddedSuccess = null // Callback opcional para quando a tarefa é adicionada com sucesso
}) => {
  const titleInputRef = useRef(null);
  const agentSelectRef = useRef(null);
  const { authenticatedUser } = useAuthenticatedUser();
  const [agentPhoto, setAgentPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Estados para manipulação de visualização de modais
  const [showDaysToCompleteModal, setShowDaysToCompleteModal] = useState(false);

  // Estados para controlar quais campos estão sendo editados
  const [showAgentInput, setShowAgentInput] = useState(false);

  const cardRef = useRef(null);

  const initialState = {
    title: '',
    days_to_complete: null, // padrão agora é null
    agent: { id: '' },
    description: '',
    status: false,
    isGroupTitle: false
  };

  const [taskData, setTaskData] = useState(initialState);

  const isBusy = isLoading || externalLoading;

  // Focus on title input when component mounts
  useEffect(() => {
    // Usar setTimeout para garantir que o foco seja aplicado após a renderização
    setTimeout(() => {
      if (titleInputRef.current && !isBusy) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    }, 0);

    if (authenticatedUser?.id) {
      setCurrentUserId(authenticatedUser.id);
    }
  }, []);

  useEffect(() => {
    setTaskData(prev => ({
      ...prev,
      isGroupTitle: isGroupTitle
    }));
  }, [isGroupTitle]);

  // Effect for focusing agent input when it becomes visible
  useEffect(() => {
    if (showAgentInput && agentSelectRef.current && !isBusy) {
      // Usar setTimeout para garantir que o foco seja aplicado após a renderização
      setTimeout(() => {
        if (agentSelectRef.current && !isBusy) {
          agentSelectRef.current.focus();
        }
      }, 0);
    }
  }, [showAgentInput, isBusy]);

  useEffect(() => {
    if (currentUserId) {
      fetchAgentPhoto(currentUserId);
      // Set current user as default agent
      setTaskData(prev => ({
        ...prev,
        agent: { id: currentUserId }
      }));
    }
  }, [currentUserId]);

  useEffect(() => {
    if (taskData.agent?.id) {
      fetchAgentPhoto(taskData.agent.id);
    }
  }, [taskData.agent?.id]);

  // Effect para focar no input quando os dados são limpos (após adicionar uma tarefa)
  useEffect(() => {
    // Se o título está vazio e não está ocupado, focar no input
    if (taskData.title === '' && !isBusy && titleInputRef.current) {
      // Usar setTimeout para garantir que o foco seja aplicado após a renderização
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
          titleInputRef.current.select();
        }
      }, 10);
    }
  }, [taskData.title, isBusy]);

  // Effect para focar no input quando o componente não está mais ocupado
  useEffect(() => {
    if (!isBusy && titleInputRef.current && taskData.title === '') {
      // Focar no input quando o componente não está mais ocupado
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
          titleInputRef.current.select();
        }
      }, 50);
    }
  }, [isBusy, taskData.title]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (showDaysToCompleteModal) {
        return;
      }

      if (isBusy) {
        return;
      }

      // Verificar se o clique foi fora do card
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        // Verificar se o clique não foi em um elemento que deveria manter o foco
        const target = event.target;
        const isDropdown =
          target.closest('.dropdown-menu') || target.closest('.dropdown');
        const isModal =
          target.closest('.modal') || target.closest('.modal-content');
        const isTooltip =
          target.closest('.tooltip') || target.closest('.popover');
        const isFormControl =
          target.closest('.form-control') ||
          target.closest('input') ||
          target.closest('select');
        const isButton =
          target.closest('button') || target.tagName === 'BUTTON';
        const isSpinner =
          target.closest('.spinner') || target.closest('.spinner-border');
        const isCard = target.closest('.card') || target.closest('.task');
        const isTaskList =
          target.closest('.task-list') || target.closest('.simple-task-list');
        const isHeader = target.closest('header');
        const isBulkActions = target.closest('.bulk-actions');

        // Não fechar se o clique foi em um elemento de formulário, botão, spinner, card, task list, header, bulk actions ou se o componente está ocupado
        if (
          !isDropdown &&
          !isModal &&
          !isTooltip &&
          !isFormControl &&
          !isButton &&
          !isSpinner &&
          !isCard &&
          !isTaskList &&
          !isHeader &&
          !isBulkActions &&
          !isBusy
        ) {
          onCancel();
        }
      }
    };

    // Só adicionar o listener se o componente não estiver ocupado
    if (!isBusy) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel, showDaysToCompleteModal, isBusy]);

  useEffect(() => {
    if (authenticatedUser?.id) {
      setCurrentUserId(authenticatedUser.id);
    } else if (agents.length > 0) {
      setCurrentUserId(agents[0].id);
    }
  }, [authenticatedUser, agents]);

  const fetchAgentPhoto = async agentId => {
    if (!agentId) {
      setAgentPhoto(null);
      return;
    }

    try {
      setIsLoading(true);
      const response = await AgentPhotoService.find(agentId);
      if (response.ok) {
        const { url } = await response.json();
        setAgentPhoto(url);
      } else {
        setAgentPhoto(null); // Fallback to initials if photo not found
      }
    } catch (error) {
      console.log(`Foto não encontrada para o agente ${agentId}`);
      setAgentPhoto(null); // Fallback to initials on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setTaskData(prev => ({
      ...prev,
      [field]: field === 'agent' ? { id: parseInt(value) || '' } : value
    }));
  };

  const handleSubmit = async () => {
    if (!taskData.title.trim() || isBusy) {
      return;
    }

    setIsLoading(true);
    try {
      // Encontra o agente completo baseado no ID selecionado
      const selectedAgent = taskData.agent?.id
        ? agents.find(agent => agent.id === taskData.agent.id)
        : null;

      const newTask = {
        ...taskData,
        title: taskData.title.trim(),
        agent: selectedAgent,
        days_to_complete:
          taskData.days_to_complete === '' ||
          taskData.days_to_complete === undefined
            ? null
            : Number.isNaN(Number(taskData.days_to_complete))
            ? null
            : taskData.days_to_complete === null
            ? null
            : parseInt(taskData.days_to_complete),
        description: taskData.description?.trim() || ''
      };

      if (!externalLoading) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      onTaskAdded(newTask);

      // Chamar callback de sucesso se fornecido
      if (onTaskAddedSuccess) {
        onTaskAddedSuccess();
      }

      // Limpar os dados imediatamente e focar no input
      startNewTask();

      // Garantir que o foco seja mantido após um pequeno delay
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
          titleInputRef.current.select();
        }
      }, 100);
    } catch (error) {
      console.error('Erro ao adicionar tarefa:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (isBusy) return;

    if (e.key === 'Enter') {
      if (showAgentInput) {
        setShowAgentInput(false);
      } else {
        handleSubmit();
      }
    } else if (e.key === 'Escape') {
      if (showAgentInput) {
        setShowAgentInput(false);
      } else {
        onCancel();
      }
    }
  };

  const startNewTask = () => {
    setTaskData({
      ...initialState,
      isGroupTitle: isGroupTitle,
      agent: { id: currentUserId }
    });
    setShowAgentInput(false);
    setAgentPhoto(null);

    // Focar no input imediatamente após limpar os dados
    if (titleInputRef.current) {
      titleInputRef.current.focus();
      // Selecionar todo o texto para facilitar a digitação
      titleInputRef.current.select();
    }
  };

  // Função para obter o nome do agente atual
  const getCurrentAgentName = () => {
    if (!taskData.agent?.id || !agents) return '';
    const agent = agents.find(a => a.id === taskData.agent.id);
    return agent ? `${agent.firstName} ${agent.lastName}` : '';
  };

  const getDaysToCompleteColor = days => {
    if (days === null) {
      return 'text-secondary';
    }
    if (days === 0) {
      return 'text-info';
    }
    if (days > 0) {
      return 'text-success';
    }
    return '';
  };

  return (
    <>
      <TemplateDaysToCompleteEditModal
        show={showDaysToCompleteModal}
        close={() => setShowDaysToCompleteModal(false)}
        taskData={taskData}
        setTaskData={setTaskData}
      />
      <Card
        ref={cardRef}
        role="button"
        className={`border-0 shadow-sm mb-2 ${isBusy ? 'opacity-75' : ''}`}
        style={{
          borderRadius: '8px'
        }}
      >
        <Card.Body
          as={Flex}
          justifyContent="between"
          alignItems="center"
          className="py-2 px-3"
          style={{ minHeight: '64px' }}
        >
          <Flex alignItems="center" className="flex-1">
            {!isGroupTitle && (
              <Button
                variant="link"
                size="sm"
                className="me-2"
                style={{
                  width: '24px',
                  height: '24px',
                  padding: '0',
                  color: '#e0e0e0',
                  border: '1px solid #e0e0e0',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                disabled
              >
                <FontAwesomeIcon icon={faCheck} className="fs-11" />
              </Button>
            )}

            {/* Input de título */}
            <Form.Control
              ref={titleInputRef}
              value={taskData.title}
              onChange={e => handleInputChange('title', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isGroupTitle
                  ? 'Digite o título do grupo...'
                  : 'Digite o título da tarefa...'
              }
              size={isGroupTitle ? 'lg' : 'sm'}
              className="border-0 shadow-none"
              disabled={isBusy}
            />

            {/* Indicador de loading */}
            {isBusy && (
              <div className="ms-2 d-flex align-items-center">
                <Spinner size="sm" animation="border" />
              </div>
            )}
          </Flex>

          {!isGroupTitle && (
            <Flex alignItems="center" className="task-actions">
              {/* Dias para Conclusão - mostra o valor ou o ícone */}
              <Button
                variant="link"
                className={`mx-3 p-0 fs-10 text-decoration-none ${getDaysToCompleteColor(
                  taskData.days_to_complete
                )}`}
                onClick={() => setShowDaysToCompleteModal(true)}
                disabled={isBusy}
              >
                {taskData.days_to_complete === null ? (
                  <span className="me-1">Sem prazo</span>
                ) : taskData.days_to_complete === 0 ? (
                  <>
                    <span className="me-1">Data de criação do ticket</span>{' '}
                    <FontAwesomeIcon icon={faCalendar} />
                  </>
                ) : taskData.days_to_complete > 0 ? (
                  `${taskData.days_to_complete} dias`
                ) : (
                  <FontAwesomeIcon icon={faCalendar} />
                )}
              </Button>

              {/* Tooltip para descrição */}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-description-new">
                    {taskData.description || 'Descrição da tarefa'}
                  </Tooltip>
                }
              >
                <Button
                  variant="link"
                  className="mx-3 p-0 text-secondary"
                  disabled={isBusy}
                >
                  <FontAwesomeIcon icon={faAlignJustify} />
                </Button>
              </OverlayTrigger>

              {/* Avatar/seleção de agente */}
              {showAgentInput ? (
                <Form.Select
                  ref={agentSelectRef}
                  size="sm"
                  value={taskData.agent.id}
                  onChange={e => handleInputChange('agent', e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => setShowAgentInput(false)}
                  className="mx-3"
                  style={{ width: '150px' }}
                  disabled={isBusy}
                >
                  <option value="">Sem responsável</option>
                  {agents?.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.firstName} {agent.lastName}
                    </option>
                  ))}
                </Form.Select>
              ) : taskData.agent?.id ? (
                <div
                  onClick={() => !isBusy && setShowAgentInput(true)}
                  role="button"
                  className="mx-3"
                  style={{ cursor: isBusy ? 'not-allowed' : 'pointer' }}
                >
                  <Avatar src={agentPhoto} name={getCurrentAgentName()} />
                </div>
              ) : (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mx-3 rounded-circle"
                  style={{
                    width: '30px',
                    height: '30px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => !isBusy && setShowAgentInput(true)}
                  disabled={isBusy}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="fs-11" />
                </Button>
              )}

              {/* Botão de contexto com dropdown */}
              <Dropdown className="btn-reveal-trigger opacity-100" align="end">
                <Dropdown.Toggle
                  as={Button}
                  variant="link"
                  className="p-0 text-secondary ms-2"
                  disabled={isBusy}
                >
                  <FontAwesomeIcon icon={faEllipsisH} />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item disabled>Editar</Dropdown.Item>
                  <Dropdown.Item className="text-danger" disabled>
                    Excluir
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Flex>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default TemplateNewTaskCard;
